import { useMemo, useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, ReferenceLine, CartesianGrid, XAxis, YAxis, Tooltip as ChartTooltip,
} from 'recharts';
import { SectionCard, fmtNum, fmtTRY } from '../yeni-design/primitives';
import { demoTheme } from '../../theme';
import type { Donem, SatisDonemi } from './donem';
import { CUBUK_ETIKET, cubukBoyu, kovala, type CubukBoyu } from './cubukBoyu';
import type { SatisKaynaklari } from './ekranKaynaklari';
import { parametreBildiriyor, useSaSorgu } from './useSatisVerisi';
import { KanalKapsamNotu, KapsamNotu } from './KapsamNotu';
import { BlokIskeleti } from './BlokIskeleti';
import { fisOzeti, sayi, type KanalSatiri, type Sayisal } from './ozetHesaplari';

interface SaatlikSatiri {
  saat: Sayisal;
  fis_sayisi: Sayisal;
  ciro: Sayisal;
}

export interface Nokta {
  etiket: string;
  ciro: number;
  /** Cok gunlu seride ISO gun ('YYYY-MM-DD'); saatlik seride yok. Gun detayi (K7) bunu acar. */
  gun?: string;
}

/** ISO tarih -> GG.AA.YYYY (sayfa basligindaki aralikla ayni bicim). Cozulemeyen HAM doner. */
export function tarihEtiketi(ham: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ham);
  if (!m) return ham;
  return `${m[3]}.${m[2]}.${m[1]}`;
}

/** 990002 SQL 137'den sonra 'HH:00' doner; eski sayisal saat de kabul edilir. */
export function saatEtiketi(ham: unknown): string {
  const s = String(ham ?? '');
  return /^\d{1,2}$/.test(s) ? `${s.padStart(2, '0')}:00` : s;
}

export function ortalama(noktalar: Nokta[]): number | null {
  if (noktalar.length === 0) return null;
  return noktalar.reduce((t, n) => t + n.ciro, 0) / noktalar.length;
}

interface Props {
  kaynaklar: Pick<SatisKaynaklari, 'kanal040' | 'saatlik002'>;
  donem: Donem;
  sube?: string | null;
  kanal?: string | null;
  /** K7 (G2b): YALNIZ cok gunlu gorunumde gun detayini acar (tek gun/saatlik gorunumde yok). */
  onGunSec?: (gun: string) => void;
  /** S295: cubuk boyu secenekleri donemin ADINA baglidir (`cubukBoyu`); verilmezse Ozel gibi uzunluga bakilir. */
  donemTuru?: SatisDonemi;
}

const BOY_ALT_BASLIK: Record<CubukBoyu, string> = { gun: 'Günlük ciro', hafta: 'Haftalık ciro', ay: 'Aylık ciro' };

/**
 * Satir 2 — trend (5/12). Cok gunlu: 990040 gunluk Σ ciro sutunlari + ortalama cizgisi (040 anahtari
 * ozet/kanal/sube ile paylasilir; sube/kanal istemcide). Gunluk/Dun: 990002 saatlik, `@KANAL` ile.
 * Ortalama cizgisi: toplu fatura gunleri (ör. 31 Agustos) trendi ezer (spec §9).
 */
export function ZamanBlok({ kaynaklar, donem, sube = null, kanal = null, onGunSec, donemTuru }: Props) {
  const saatlikMi = donem.tekGun;
  /* S295: cubuk boyu. Secim donem ANAHTARIYLA birlikte tutulur: donem degisince (ya da secim yeni donemin
     seceneklerinde yoksa) varsayilana doner; effect yok, gecis anindaki yanlis bir kare de yok. */
  const kural = useMemo(
    () => cubukBoyu(donemTuru ?? 'ozel', { bas: donem.bas, bit: donem.bit }),
    [donemTuru, donem.bas, donem.bit],
  );
  const donemAnahtari = `${donemTuru ?? 'ozel'}|${donem.bas}|${donem.bit}`;
  const [secim, setSecim] = useState<{ anahtar: string; boy: CubukBoyu } | null>(null);
  const boy: CubukBoyu =
    secim && secim.anahtar === donemAnahtari && kural.secenekler.includes(secim.boy) ? secim.boy : kural.varsayilan;
  // 040 TEK anahtar (final-fix F-3a): sube/kanal ASLA parametre olmaz, ds bildirse bile — istemcide suzulur.
  const gunlukQ = useSaSorgu<KanalSatiri>(kaynaklar.kanal040, { donem, sube: null, kanal: null }, !saatlikMi);
  const saatlikQ = useSaSorgu<SaatlikSatiri>(kaynaklar.saatlik002, { donem, sube, kanal }, saatlikMi);

  const noktalar = useMemo<Nokta[]>(() => {
    if (saatlikMi) {
      return (saatlikQ.data?.data ?? []).map((r) => ({ etiket: saatEtiketi(r.saat), ciro: sayi(r.ciro) }));
    }
    const gunluk = fisOzeti(gunlukQ.data?.data ?? [], sube, kanal).gunluk;
    // Hafta/ay ayni gunluk satirlardan istemcide toplanir; gun boyu bugunku seri (veri gelmeyen gun cubuk olmaz).
    if (boy !== 'gun') return kovala(gunluk, { bas: donem.bas, bit: donem.bit }, boy);
    return gunluk.map((g) => ({
      etiket: tarihEtiketi(g.tarih),
      ciro: g.ciro,
      gun: g.tarih,
    }));
  }, [saatlikMi, saatlikQ.data, gunlukQ.data, sube, kanal, boy, donem.bas, donem.bit]);

  const kaynak = saatlikMi ? kaynaklar.saatlik002 : kaynaklar.kanal040;
  const yukleniyor = saatlikMi ? saatlikQ.isLoading : gunlukQ.isLoading;
  const baslik = saatlikMi ? 'Saatlik satış dağılımı' : 'Ciro trendi';

  if (!kaynak) return null;
  if (yukleniyor) return <BlokIskeleti testid="blok-zaman-iskelet" baslik={baslik} yukseklik={240} />;
  if (noktalar.length === 0) return null;

  const ort = ortalama(noktalar);
  // Gun detayi yalniz gun cubugunda anlamli: hafta/ay cubugu bir gun degil.
  const gunSecilebilir = !saatlikMi && boy === 'gun' && onGunSec !== undefined;
  const boySecici = !saatlikMi && kural.secenekler.length > 1 && (
    <div role="group" aria-label="Çubuk boyu" data-testid="zaman-boy" className="inline-flex rounded-md border border-border overflow-hidden text-[12px]">
      {kural.secenekler.map((b) => (
        <button
          key={b}
          type="button"
          data-testid={`zaman-boy-${b}`}
          aria-pressed={boy === b}
          onClick={() => setSecim({ anahtar: donemAnahtari, boy: b })}
          className={`focus-ring px-2.5 py-1 ${boy === b ? 'bg-ink-900 text-white' : 'bg-white text-ink-700'}`}
        >
          {CUBUK_ETIKET[b]}
        </button>
      ))}
    </div>
  );
  const gunNoktalari = gunSecilebilir
    ? noktalar.filter((n): n is Nokta & { gun: string } => typeof n.gun === 'string')
    : [];

  return (
    <div data-testid="blok-zaman">
      {/* Kullanici 2026-09-15: grafik karti doldurmali. HUCRE karti komsusunun boyuna uzatir (section flex-1);
          section flex sutun + govde flex-1 + grafik kutusu flex-1 -> grafik kalan yuksekligi alir, en az 200 px. */}
      <SectionCard
        className="flex flex-col"
        bodyClass="p-5 flex-1 flex flex-col"
        title={baslik}
        actions={boySecici || undefined}
        subtitle={
          <>
            {saatlikMi ? 'Günün saatlerine göre ciro' : BOY_ALT_BASLIK[boy]} · ortalama{' '}
            <span data-testid="zaman-ortalama">{ort === null ? '—' : fmtTRY(ort)}</span>
            {saatlikMi && <KapsamNotu sube={sube} destekli={parametreBildiriyor(kaynaklar.saatlik002, '@LOKASYON')} />}
            {/* Cok gunlu 040 kanali istemcide suzer: not yalniz saatlik 002 icin anlamli. */}
            <KanalKapsamNotu
              kanal={saatlikMi ? kanal : null}
              destekli={parametreBildiriyor(kaynaklar.saatlik002, '@KANAL')}
            />
          </>
        }
      >
        <div data-testid="zaman-grafik" className="flex-1 min-h-[200px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={noktalar} margin={{ top: 4, right: 12, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="2 4" stroke={demoTheme.border} vertical={false} />
              <XAxis
                dataKey="etiket"
                tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                axisLine={{ stroke: demoTheme.border }}
                tickLine={false}
                minTickGap={24}
                interval="preserveStartEnd"
              />
              <YAxis
                tickFormatter={(v) => fmtNum(Number(v))}
                tick={{ fontSize: 11, fill: demoTheme.textMuted }}
                axisLine={false}
                tickLine={false}
                width={64}
              />
              <ChartTooltip formatter={(v) => fmtTRY(Number(v))} labelFormatter={(l) => String(l)} />
              <Bar
                dataKey="ciro"
                fill={demoTheme.primary}
                radius={[2, 2, 0, 0]}
                isAnimationActive={false}
                cursor={gunSecilebilir ? 'pointer' : undefined}
                onClick={
                  gunSecilebilir
                    ? (_veri, i) => {
                        const g = noktalar[i]?.gun;
                        if (g) onGunSec?.(g);
                      }
                    : undefined
                }
              />
              {ort !== null && <ReferenceLine y={ort} stroke={demoTheme.warning} strokeDasharray="4 4" />}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        {/* Cubuk tiklamasi fareye ozgu; klavye ve ekran okuyucu icin ayni gunler dugme listesi. Gorunmez,
            odak girince gorunur olur (Tab ile dolasan kullanici hangi gunde oldugunu gorsun). */}
        {gunNoktalari.length > 0 && (
          <ul
            data-testid="gun-detay-listesi"
            aria-label="Gün ayrıntısı"
            className="sr-only focus-within:not-sr-only focus-within:mt-2 focus-within:flex focus-within:flex-wrap focus-within:gap-1"
          >
            {gunNoktalari.map((n) => (
              <li key={n.gun}>
                <button
                  type="button"
                  data-testid={`gun-detay-${n.gun}`}
                  aria-label={`${n.etiket} günü ayrıntısı`}
                  onClick={() => onGunSec?.(n.gun)}
                  className="focus-ring text-[11px] px-1.5 py-0.5 rounded border border-border text-ink-700"
                >
                  {n.etiket}
                </button>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
    </div>
  );
}
